import { useMemo, useCallback } from "react";
import type { SalesTransaction, LeaderboardEntry } from "../types";
import { parseOutcome } from "./useOperationsAnalytics";
import type { AscensionLead } from "./useOperationsAnalytics";
import { toDisplayCurrency } from "../utils/formatters";

// =============================================================================
// useMetricSamples - METRIC SAMPLE INDEXER
// =============================================================================
// PURPOSE: Fetches real transaction samples for metric tooltips.
//
// REQUIREMENTS:
// - Shows 5 example transactions per metric definition
// - Powers interactive info icons (ⓘ) on stat cards
// - Single-pass indexing into category buckets for performance
//
// KEY DECISIONS:
// - Transactions already sorted desc by timestamp (no re-sorting needed)
// - Pre-builds category buckets (shows, newDeals, installments, etc.) once
// - DM metrics return placeholder (aggregated via RPC, no individual samples)
// - Formats currency, dates, and values for display
// =============================================================================

export interface MetricSample {
  label: string;
  value: string;
  date: string;
}

function fmtDate(d: string, tz?: string): string {
  const date = new Date(d);
  if (isNaN(date.getTime())) return "--";
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    ...(tz ? { timeZone: tz } : {}),
  });
}

function fmtCurrency(v: number): string {
  // toDisplayCurrency applies the exec USD->AUD toggle (identity in USD mode).
  return `$${toDisplayCurrency(v).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
}

function take5<T>(arr: T[]): T[] {
  return arr.slice(0, 5);
}

interface IndexedBuckets {
  allTxns: SalesTransaction[];
  salesOutcomes: SalesTransaction[];
  lowTickets: SalesTransaction[];
  shows: SalesTransaction[];
  newDeals: SalesTransaction[];
  installments: SalesTransaction[];
  setterCalls: SalesTransaction[];
  closerCalls: SalesTransaction[];
  withCash: SalesTransaction[];
  withRevenue: SalesTransaction[];
  allDeals: SalesTransaction[];
  parsedOutcomes: Map<SalesTransaction, ReturnType<typeof parseOutcome>>;
}

export function useMetricSamples(
  filteredTransactions: SalesTransaction[],
  leaderboard: LeaderboardEntry[],
  ascensionLeads: AscensionLead[],
  timezone?: string
) {
  // Single-pass indexing into category buckets (already sorted desc by timestamp)
  const indexed = useMemo<IndexedBuckets>(() => {
    const allTxns = filteredTransactions;
    const salesOutcomes: SalesTransaction[] = [];
    const lowTickets: SalesTransaction[] = [];
    const shows: SalesTransaction[] = [];
    const newDeals: SalesTransaction[] = [];
    const installments: SalesTransaction[] = [];
    const setterCalls: SalesTransaction[] = [];
    const closerCalls: SalesTransaction[] = [];
    const withCash: SalesTransaction[] = [];
    const withRevenue: SalesTransaction[] = [];
    const parsedOutcomes = new Map<SalesTransaction, ReturnType<typeof parseOutcome>>();

    for (const t of allTxns) {
      if ((Number(t.cashCollected) || 0) > 0) withCash.push(t);
      if ((Number(t.revenueClosed) || 0) > 0) withRevenue.push(t);

      if (t.eventType === "low_ticket_purchase") {
        lowTickets.push(t);
        continue;
      }
      if (t.eventType !== "sales_call_outcome") continue;

      salesOutcomes.push(t);
      const parsed = parseOutcome(t.callOutcome);
      parsedOutcomes.set(t, parsed);

      if (parsed.isShow) shows.push(t);
      if (parsed.isNewDeal) newDeals.push(t);
      if (parsed.isInstallment) installments.push(t);
      if (t.setterId) setterCalls.push(t);
      if (t.closerId) closerCalls.push(t);
    }

    // Pre-merge and sort deals (newDeals + lowTickets) to avoid duplicate work in aov/dealsClosed
    const allDeals = [...newDeals, ...lowTickets].sort(
      (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    );

    return {
      allTxns,
      salesOutcomes,
      lowTickets,
      shows,
      newDeals,
      installments,
      setterCalls,
      closerCalls,
      withCash,
      withRevenue,
      allDeals,
      parsedOutcomes,
    };
  }, [filteredTransactions]);

  const sortedLeaderboard = useMemo(() => {
    return [...leaderboard]
      .filter((e) => (e.daily_dials || 0) > 0)
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [leaderboard]);

  const getSamples = useCallback(
    (metricKey: string): MetricSample[] => {
      const { parsedOutcomes } = indexed;

      switch (metricKey) {
        // --- Financial ---
        case "cashCollected":
          return take5(indexed.withCash).map((t) => ({
            label: t.clientName || "Unknown",
            value: fmtCurrency(Number(t.cashCollected) || 0),
            date: fmtDate(t.timestamp, timezone),
          }));

        case "revenueClosed":
          return take5(indexed.withRevenue).map((t) => ({
            label: t.clientName || "Unknown",
            value: fmtCurrency(Number(t.revenueClosed) || 0),
            date: fmtDate(t.timestamp, timezone),
          }));

        case "aov":
          // AOV = HIGH-TICKET deals only. R63 (Mako 2026-09-15): a payment plan is
          // ALWAYS high-ticket (any deposit); a paid_in_full under $2,000 cash is a
          // low-ticket downsell, excluded from AOV. Matches the SQL ht_deals gate
          // (status='closed_payment_plan' OR cash_collected >= 2000) in get_operations_kpis.
          return take5(
            indexed.newDeals.filter((t) => {
              const parsed = parsedOutcomes.get(t);
              return Boolean(parsed?.isPaymentPlan) || (Number(t.cashCollected) || 0) >= 2000;
            })
          ).map((t) => ({
            label: t.clientName || "Unknown",
            value: fmtCurrency(Number(t.cashCollected) || 0),
            date: fmtDate(t.timestamp, timezone),
          }));

        case "installmentsCollected":
        case "installmentsCount":
          return take5(indexed.installments).map((t) => ({
            label: t.clientName || "Unknown",
            value: fmtCurrency(Number(t.cashCollected) || 0),
            date: fmtDate(t.timestamp, timezone),
          }));

        case "revenueToCashRatio":
        case "slippageRate":
          return take5(indexed.withCash).map((t) => ({
            label: t.clientName || "Unknown",
            value: `${fmtCurrency(Number(t.cashCollected) || 0)} / ${fmtCurrency(Number(t.revenueClosed) || 0)}`,
            date: fmtDate(t.timestamp, timezone),
          }));

        // --- Call Metrics ---
        case "callsBooked":
          return take5(indexed.salesOutcomes).map((t) => ({
            label: t.clientName || "Unknown",
            value: t.callOutcome || "pending",
            date: fmtDate(t.timestamp, timezone),
          }));

        case "callsTaken":
        case "shows":
          return take5(indexed.shows).map((t) => ({
            label: t.clientName || "Unknown",
            value: t.callOutcome || "showed",
            date: fmtDate(t.timestamp, timezone),
          }));

        case "showUpRate":
          return take5(indexed.salesOutcomes).map((t) => ({
            label: t.clientName || "Unknown",
            value: (parsedOutcomes.get(t)?.isShow ?? true) ? "Showed" : "No Show",
            date: fmtDate(t.timestamp, timezone),
          }));

        case "closeRate":
          return take5(indexed.shows).map((t) => ({
            label: t.clientName || "Unknown",
            value: (parsedOutcomes.get(t)?.isNewDeal ?? false) ? "Won" : "Lost",
            date: fmtDate(t.timestamp, timezone),
          }));

        // --- Cash Per Call ---
        case "cashPerCallTaken":
          return take5(indexed.shows).map((t) => ({
            label: t.clientName || "Unknown",
            value: fmtCurrency(Number(t.cashCollected) || 0),
            date: fmtDate(t.timestamp, timezone),
          }));

        case "cashPerCallBooked":
          return take5(indexed.salesOutcomes).map((t) => ({
            label: t.clientName || "Unknown",
            value: fmtCurrency(Number(t.cashCollected) || 0),
            date: fmtDate(t.timestamp, timezone),
          }));

        // --- Ticket Tiers ---
        case "highTicket.cash":
          return take5(indexed.newDeals).map((t) => ({
            label: t.clientName || "Unknown",
            value: fmtCurrency(Number(t.cashCollected) || 0),
            date: fmtDate(t.timestamp, timezone),
          }));

        case "highTicket.revenue":
          return take5(indexed.newDeals).map((t) => ({
            label: t.clientName || "Unknown",
            value: fmtCurrency(Number(t.revenueClosed) || 0),
            date: fmtDate(t.timestamp, timezone),
          }));

        case "lowTicket.cash":
          return take5(indexed.lowTickets).map((t) => ({
            label: t.clientName || "Unknown",
            value: fmtCurrency(Number(t.cashCollected) || 0),
            date: fmtDate(t.timestamp, timezone),
          }));

        case "lowTicket.revenue":
          return take5(indexed.lowTickets).map((t) => ({
            label: t.clientName || "Unknown",
            value: fmtCurrency(Number(t.revenueClosed) || 0),
            date: fmtDate(t.timestamp, timezone),
          }));

        case "ascensions":
          return take5(ascensionLeads).map((a) => ({
            label: a.leadName,
            value: `${a.lowTicketProduct} → ${a.upsellProduct}`,
            date: fmtDate(a.upsellDate, timezone),
          }));

        // --- Setter / Closer ---
        case "setterBookedCalls":
          return take5(indexed.setterCalls).map((t) => ({
            label: t.clientName || "Unknown",
            value: t.setterName || "Setter",
            date: fmtDate(t.timestamp, timezone),
          }));

        case "closerBookedCalls":
          return take5(indexed.closerCalls).map((t) => ({
            label: t.clientName || "Unknown",
            value: t.closerName || "Closer",
            date: fmtDate(t.timestamp, timezone),
          }));

        case "setterQualityScore":
          return take5(indexed.salesOutcomes.filter((t) => t.setterQaScore != null)).map((t) => ({
            label: t.clientName || "Unknown",
            value: `${t.setterQaScore}/10`,
            date: fmtDate(t.timestamp, timezone),
          }));

        case "conversationDepth":
          return take5(indexed.salesOutcomes.filter((t) => t.avgConversationDepth != null)).map(
            (t) => ({
              label: t.clientName || "Unknown",
              value: `${t.avgConversationDepth} min`,
              date: fmtDate(t.timestamp, timezone),
            })
          );

        case "avgResponseTime":
          return take5(indexed.salesOutcomes.filter((t) => t.avgResponseTimeSeconds != null)).map(
            (t) => ({
              label: t.clientName || "Unknown",
              value: `${Math.round((Number(t.avgResponseTimeSeconds) || 0) / 60)} min`,
              date: fmtDate(t.timestamp, timezone),
            })
          );

        // --- Dialing (from leaderboard) ---
        case "dials":
        case "dialsPerDay":
        case "dialsConvoPerCall":
          return sortedLeaderboard.slice(0, 5).map((e) => ({
            label: e.rep_name || e.rep_email,
            value: `${e.daily_dials} dials`,
            date: fmtDate(e.date, timezone),
          }));

        // --- Funnel ---
        case "dealsClosed":
          return take5(indexed.newDeals).map((t) => ({
            label: t.clientName || "Unknown",
            value: t.callOutcome || "deal",
            date: fmtDate(t.timestamp, timezone),
          }));

        case "funnelQuality":
          return take5(indexed.salesOutcomes).map((t) => ({
            label: t.clientName || "Unknown",
            value: (parsedOutcomes.get(t)?.isNewDeal ?? false) ? "Closed" : t.callOutcome,
            date: fmtDate(t.timestamp, timezone),
          }));

        // --- Instagram DM Metrics (aggregated from RPC, no individual samples) ---
        case "newDMChats":
        case "conversations":
        case "newDMsPerDay":
        case "newDMsPerConvo":
        case "dmConvosPerDay":
        case "dialsPerConvo":
        case "qualityOfProspects":
          // DM metrics are aggregated via RPC from ghl_conversations/messages tables
          // Individual conversation samples would require additional data fetching
          return [{ label: "Source", value: "Instagram DMs (GHL sync)", date: "Aggregated" }];

        // --- Placeholders ---
        default:
          return [];
      }
    },
    [indexed, sortedLeaderboard, ascensionLeads, timezone]
  );

  return { getSamples };
}
